import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import useApiHelper from "../hooks/useApiHelper";
import { IDocumentResponse } from "../models/Document";
import {
  ICheckInItemsResponse,
  ICheckInStepsResponse,
  ICreateCheckInRequest,
  ICreateUpdateCheckInItemRequest,
  ICreateUpdateCheckInStepRequest,
  ISingleCheckInItemResponse,
  ISingleCheckInResponse,
  ISingleCheckInStepResponse,
} from "../models/CheckIn";

export default function useCheckIn(vehicleId?: string, checkInId?: string, includeAllSteps = false) {
  const { get, getFile, post, postDocs, put, del } = useApiHelper();
  const queryClient = useQueryClient();

  const checkIn = useQuery({
    queryKey: ["checkin", checkInId],
    queryFn: () => get<ISingleCheckInResponse>(`/checkin/${checkInId}`),
    enabled: !!checkInId,
    staleTime: 1000 * 60 * 2,
  });

  const checkInByVehicle = useQuery({
    queryKey: ["checkin-vehicle", vehicleId],
    queryFn: async () => {
      try {
        return await get<ISingleCheckInResponse>(`/checkin/vehicle/${vehicleId}`);
      } catch {
        return null;
      }
    },
    enabled: false,
    staleTime: 1000 * 60 * 2,
    retry: false,
  });

  const checkInItems = useQuery({
    queryKey: ["checkin-items", checkInId],
    queryFn: () => get<ICheckInItemsResponse>(`/checkin/${checkInId}/items`),
    enabled: !!checkInId,
    staleTime: 1000 * 60,
  });

  const checkInSteps = useQuery({
    queryKey: ["checkin-steps"],
    queryFn: () => get<ICheckInStepsResponse>(`/checkin/steps?activeOnly=true`),
    staleTime: 1000 * 60 * 10,
  });

  const checkInDocuments = useQuery({
    queryKey: ["checkin-documents", checkInId],
    queryFn: () => get<IDocumentResponse>(`/checkin/${checkInId}/documents`),
    enabled: !!checkInId,
    staleTime: 1000 * 60,
  });

  const allCheckInSteps = useQuery({
    queryKey: ["checkin-steps-all"],
    queryFn: () => get<ICheckInStepsResponse>(`/checkin/steps`),
    enabled: includeAllSteps,
    staleTime: 1000 * 60 * 10,
  });

  const createCheckIn = useMutation(
    (body: ICreateCheckInRequest) => post<ICreateCheckInRequest, ISingleCheckInResponse>("/checkin", body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["checkin", checkInId]);
        queryClient.invalidateQueries(["checkin-vehicle", vehicleId]);
        queryClient.invalidateQueries(["vehicles", vehicleId]);
      },
    }
  );

  const saveCheckInItem = useMutation(
    (body: ICreateUpdateCheckInItemRequest) => post<ICreateUpdateCheckInItemRequest, ISingleCheckInItemResponse>("/checkin/items", body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["checkin", checkInId]);
        queryClient.invalidateQueries(["checkin-vehicle", vehicleId]);
        queryClient.invalidateQueries(["checkin-items", checkInId]);
        queryClient.invalidateQueries(["vehicles", vehicleId]);
        queryClient.invalidateQueries(["lead"]);
      },
    }
  );

  const createCheckInStep = useMutation(
    (body: ICreateUpdateCheckInStepRequest) => post<ICreateUpdateCheckInStepRequest, ISingleCheckInStepResponse>("/checkin/steps", body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["checkin-steps"]);
        queryClient.invalidateQueries(["checkin-steps-all"]);
      },
    }
  );

  const updateCheckInStep = useMutation(
    (body: ICreateUpdateCheckInStepRequest) => put<ICreateUpdateCheckInStepRequest, ISingleCheckInStepResponse>(`/checkin/steps/${body.id}`, body),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["checkin-steps"]);
        queryClient.invalidateQueries(["checkin-steps-all"]);
      },
    }
  );

  const updateCheckInStepOrder = useMutation(
    ({ checkInStepId, newIndex }: { checkInStepId: string, newIndex: number }) => put(`/checkin/steps/${checkInStepId}/order?newIndex=${newIndex}`, undefined),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["checkin-steps"]);
        queryClient.invalidateQueries(["checkin-steps-all"]);
      },
    }
  );

  const deleteCheckInStep = useMutation(
    (checkInStepId: string) => del(`/checkin/steps/${checkInStepId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["checkin-steps"]);
        queryClient.invalidateQueries(["checkin-steps-all"]);
      },
    }
  );

  const uploadCheckInItemDocuments = useMutation(
    ({ checkInItemId, formData }: { checkInItemId: string, formData: FormData }) => postDocs(`/checkin/items/${checkInItemId}/documents`, formData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["checkin-documents", checkInId]);
        queryClient.invalidateQueries(["leadsDocuments"]);
        queryClient.invalidateQueries(["vehicles", vehicleId]);
      },
    }
  );

  const downloadCheckInItemDocument = useMutation(
    ({ checkInItemId, documentId }: { checkInItemId: string, documentId: string }) => getFile(`/checkin/items/${checkInItemId}/documents/${documentId}`),
    {
      onSuccess: (data: any) => {
        return data;
      },
    }
  );

  const deleteCheckInItemDocument = useMutation(
    ({ checkInItemId, documentId }: { checkInItemId: string, documentId: string }) => del(`/checkin/items/${checkInItemId}/documents/${documentId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(["checkin-documents", checkInId]);
        queryClient.invalidateQueries(["leadsDocuments"]);
        queryClient.invalidateQueries(["vehicles", vehicleId]);
      },
    }
  );

  return {
    checkIn,
    checkInByVehicle,
    checkInItems,
    checkInSteps,
    checkInDocuments,
    allCheckInSteps,
    createCheckIn,
    saveCheckInItem,
    createCheckInStep,
    updateCheckInStep,
    updateCheckInStepOrder,
    deleteCheckInStep,
    uploadCheckInItemDocuments,
    downloadCheckInItemDocument,
    deleteCheckInItemDocument,
  };
}
